import { useTranslation } from '@mypoz/i18n';
import { awaitingPaymentCount, confirmReceiptCount, readyToOrderCount } from '@mypoz/shared';

import { DASHBOARD_TILE_ROUTES } from '@/features/dashboard/statuses.js';
import { LatestNotifications } from '@/features/dashboard/components/LatestNotifications.js';
import { PageTitle } from '@/components/ui.js';
import { useWarehouses } from '@/features/warehouses/index.js';
import { ShipToAddressCard } from '@/features/dashboard/components/ShipToAddressCard.js';
import { StatTile } from '@/features/dashboard/components/StatTile.js';
import { WalletBalanceCard } from '@/features/dashboard/components/WalletBalanceCard.js';
import { useNotifications } from '@/features/notifications/index.js';
import { useAccountProfile } from '@/features/account/index.js';
import { useWallet } from '@/features/wallet/index.js';
import { useEligiblePackageStatusCodes, useOrders } from '@/features/orders/index.js';
import { usePackages } from '@/features/packages/index.js';
import type { StatTileValue } from '@/features/dashboard/components/StatTile.js';
import { WALLET_BALANCE_ROUTE, WAREHOUSES_ROUTE } from '@/shell/navigation.js';

/** A tile's value from the load state of what it counts: loading and error win over a count. */
function tileValue(
  statuses: readonly ('loading' | 'ready' | 'error')[],
  count: () => number,
): StatTileValue {
  if (statuses.includes('error')) return { status: 'error' };
  if (statuses.includes('loading')) return { status: 'loading' };
  return { status: 'ready', count: count() };
}

export function Dashboard() {
  const { t } = useTranslation();
  const wallet = useWallet();
  const { profile } = useAccountProfile();
  const { warehouses, status: warehousesStatus } = useWarehouses();
  const { notifications } = useNotifications();
  /*
    The tiles count what the app already holds (MYP-27): the Packages and Orders lists are loaded
    app-wide, so the only request made here is the vendor's short list of orderable statuses.
  */
  const { packages, status: packagesStatus, reload: reloadPackages } = usePackages();
  const { orders, status: ordersStatus, reload: reloadOrders } = useOrders();
  const eligible = useEligiblePackageStatusCodes();

  const readyToOrder = tileValue([packagesStatus, eligible.status], () =>
    readyToOrderCount(packages, eligible.codes),
  );
  const awaitingPayment = tileValue([ordersStatus], () => awaitingPaymentCount(orders));
  const confirmReceipt = tileValue([ordersStatus], () => confirmReceiptCount(orders));

  const retryReadyToOrder = () => {
    if (packagesStatus === 'error') reloadPackages();
    if (eligible.status === 'error') eligible.retry();
  };

  return (
    <>
      <PageTitle title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />

      {/*
        The wallet keeps a row to itself. It is a balance, not a count of things waiting on you,
        and giving it the same width and weight as the three tiles would flatten that difference.
      */}
      <WalletBalanceCard
        balanceCents={wallet.balanceCents}
        currency={wallet.currency}
        topUpHref={WALLET_BALANCE_ROUTE}
      />

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <StatTile
          label={t('dashboard.tiles.readyToOrder.label')}
          value={readyToOrder}
          onRetry={retryReadyToOrder}
          hint={t('dashboard.tiles.readyToOrder.hint')}
          emptyHint={t('dashboard.tiles.readyToOrder.empty')}
          action={{
            label: t('dashboard.tiles.readyToOrder.cta'),
            to: DASHBOARD_TILE_ROUTES.readyToOrder,
          }}
        />
        <StatTile
          label={t('dashboard.tiles.awaitingPayment.label')}
          value={awaitingPayment}
          onRetry={reloadOrders}
          hint={t('dashboard.tiles.awaitingPayment.hint')}
          emptyHint={t('dashboard.tiles.awaitingPayment.empty')}
          action={{
            label: t('dashboard.tiles.awaitingPayment.cta'),
            to: DASHBOARD_TILE_ROUTES.awaitingPayment,
          }}
        />
        <StatTile
          label={t('dashboard.tiles.confirmReceipt.label')}
          value={confirmReceipt}
          onRetry={reloadOrders}
          hint={t('dashboard.tiles.confirmReceipt.hint')}
          emptyHint={t('dashboard.tiles.confirmReceipt.empty')}
          action={{
            label: t('dashboard.tiles.confirmReceipt.cta'),
            to: DASHBOARD_TILE_ROUTES.confirmReceipt,
          }}
        />
      </div>

      {/*
        Notifications and the ship-to address share a row, per the mentor design: one is what
        happened, the other is what you came here to copy. They stack below lg, where two columns
        would leave an address too narrow to read without wrapping mid-street.
      */}
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <LatestNotifications notifications={notifications.slice(0, 5)} />

        <ShipToAddressCard
          /*
            The first warehouse the vendor lists, which is Guangzhou and is what the legacy
            dashboard shows. Nothing in the response marks a customer's own default — there is
            no such field — so first-listed is the honest choice rather than an invented one,
            and the card links to the full list for anyone who wants a different origin.
          */
          address={warehouses[0]}
          status={warehousesStatus}
          customerCode={profile.customerId}
          allWarehousesHref={WAREHOUSES_ROUTE}
        />
      </div>
    </>
  );
}
